import React, { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate, useLocation } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./Hero.css";
import StripeSelector from "./StripeSelector";

import { styled } from "@mui/material/styles";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";

// @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
// @@@@@Searchbar Material UI Style@@@@@@
// @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@
// Design system colors (matching CSS variables)
const colors = {
  bgTertiary: "#505050",
  bgTertiaryHover: "#606060",
  borderLight: "rgba(255, 255, 255, 0.2)",
  borderLightHover: "rgba(255, 255, 255, 0.3)",
  borderLightFocus: "rgba(255, 255, 255, 0.5)",
  textPrimary: "#fff",
  textWhite70: "rgba(255, 255, 255, 0.7)",
  textWhite60: "rgba(255, 255, 255, 0.6)",
  overlayTooltip: "rgba(20, 20, 20, 0.95)",
  shadowTooltip: "0 4px 12px rgba(0, 0, 0, 0.3)",
  radiusXl: 20,
  radiusMd: 8,
};

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: colors.radiusXl,
  backgroundColor: colors.bgTertiary,
  color: colors.textPrimary,
  display: "flex",
  alignItems: "center",
  minHeight: "40px",
  border: `1px solid ${colors.borderLight}`,
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: colors.bgTertiaryHover,
    borderColor: colors.borderLightHover,
  },
  "&:focus-within": {
    backgroundColor: colors.bgTertiaryHover,
    borderColor: colors.borderLightFocus,
    boxShadow: "0 0 0 2px rgba(255, 255, 255, 0.1)",
  },
  marginLeft: 0,
  width: "100%",
  maxWidth: "500px",
  margin: "0 auto",
  [theme.breakpoints.down("sm")]: {
    marginLeft: theme.spacing(1),
    width: "auto",
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  right: 0,
  top: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1,
  cursor: "pointer",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: colors.textPrimary,
  width: "100%",
  display: "flex",
  alignItems: "center",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: theme.spacing(2),
    paddingRight: `calc(1em + ${theme.spacing(4)})`,
    color: colors.textPrimary,
    lineHeight: "1.5",
    fontSize: "14px",
    "&::placeholder": {
      color: colors.textWhite60,
      opacity: 1,
    },
    "&:focus": {
      color: colors.textPrimary,
    },
  },
}));

const Hero = (imageId) => {
  const [loading, setLoading] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const sliderRef = useRef(null);
  const [heroImage, setHeroImage] = useState([]);
  const [carouselData, setCarouselData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [allSearchData, setAllSearchData] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Keep a local reference to the last search term
  const [lastSearchTerm, setLastSearchTerm] = useState("");
  // Add a ref to track the current heroImage
  const heroImageRef = useRef([]);

  const fetchHeroImages = async () => {
    try {
      const { data, error } = await supabase
        .from('form_responses')
        .select('*')
        .not('hero_image', 'is', null);

      if (error) throw error;

      const docs = data || [];
      docs.sort((a, b) => a.legacy_id - b.legacy_id);
      setHeroImage(docs);
      heroImageRef.current = docs; // Update the ref as well
      setCarouselData(docs);
      console.log("Hero Image", docs);
    } catch (error) {
      console.error("Error fetching documents: ", error);
    } finally {
      setLoading(false);
    }
  };

  const executeSearch = async (term) => {
    // Reset slider to first slide visually before data changes?
    // if (sliderRef.current) {
    //   sliderRef.current.slickGoTo(0); 
    // } // Commenting this out as it might interfere

    setLastSearchTerm(term); // Store the current search term
    
    // If term is empty or null/undefined, show all items
    if (!term || term.trim() === "") {
      // If we don\'t have any hero images yet, fetch them
      if (heroImageRef.current.length === 0) {
        await fetchHeroImages(); // This sets carouselData
      } else {
        // Use the full dataset
        setCarouselData(heroImageRef.current);
        // REMOVED slickGoTo logic from here
      }
      return; // Important: return after handling empty search term
    }

    // Proceed with filtering if there's a search term
    setLoading(true); // Indicate loading during search
    try {
      const searchTermLower = term.toLowerCase().trim();
      // Fetch all docs for filtering
      const { data, error } = await supabase
        .from('form_responses')
        .select('*');

      if (error) throw error;

      const allDocs = data || [];

      const filteredResults = allDocs.filter((item) => {
        const title = (item.title || "").toLowerCase();
        const country = (item.country || "").toLowerCase();
        const continent = (item.continent || "").toLowerCase();
        return (
          title.includes(searchTermLower) ||
          country.includes(searchTermLower) ||
          continent.includes(searchTermLower)
        );
      });

      // Sort results after filtering
      filteredResults.sort((a, b) => a.legacy_id - b.legacy_id);

      setCarouselData(filteredResults);
      // REMOVED slickGoTo logic from here

    } catch (error) {
      console.error("Error executing search:", error);
      // Potentially set carouselData to empty array or show error message
      setCarouselData([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all search data on mount
  useEffect(() => {
    const fetchSearchData = async () => {
      try {
        const { data, error } = await supabase
          .from('form_responses')
          .select('*');

        if (error) throw error;
        setAllSearchData(data || []);
      } catch (error) {
        console.error("Error fetching search data:", error);
      }
    };
    fetchSearchData();
  }, []);

  // Load initial search term from localStorage and URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const queryParam = searchParams.get("q");

    if (queryParam) {
      setSearchTerm(queryParam);
      localStorage.setItem("searchTerm", queryParam);
      executeSearch(queryParam);
    } else {
      const storedSearchTerm = localStorage.getItem("searchTerm");
      if (storedSearchTerm) {
        setSearchTerm(storedSearchTerm);
        executeSearch(storedSearchTerm);
      } else {
        fetchHeroImages();
      }
    }
  }, [location.search]);

  // Debounced search function for dropdown
  const performSearch = useCallback((term) => {
    if (!term || term.trim() === "") {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const searchTermLower = term.toLowerCase().trim();
    const filtered = allSearchData.filter((item) => {
      const title = (item.title || "").toLowerCase();
      const country = (item.country || "").toLowerCase();
      const continent = (item.continent || "").toLowerCase();
      return (
        title.includes(searchTermLower) ||
        country.includes(searchTermLower) ||
        continent.includes(searchTermLower)
      );
    });

    // Helper function to check if a value starts with search term
    const startsWith = (value) => value.toLowerCase().startsWith(searchTermLower);
    const includes = (value) => value.toLowerCase().includes(searchTermLower);

    // Helper function to sort by priority (starts with first, then contains)
    const sortByPriority = (arr) => {
      return arr.sort((a, b) => {
        const aStarts = startsWith(a.displayValue);
        const bStarts = startsWith(b.displayValue);
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;
        return 0;
      });
    };

    // 1. Group Cities by exact Title matches
    const cityMap = new Map();
    filtered.forEach((item) => {
      const title = item.title || "";
      if (title && includes(title)) {
        if (!cityMap.has(title)) {
          cityMap.set(title, {
            displayValue: title,
            searchTerm: title,
            type: "city",
            items: [],
          });
        }
        cityMap.get(title).items.push(item);
      }
    });
    const citySuggestions = sortByPriority(Array.from(cityMap.values())).slice(0, 8);

    // 2. Group Countries by Country field
    const countryMap = new Map();
    filtered.forEach((item) => {
      const country = item.country || "";
      if (country && includes(country)) {
        if (!countryMap.has(country)) {
          countryMap.set(country, {
            displayValue: country,
            searchTerm: country,
            type: "country",
            items: [],
          });
        }
        countryMap.get(country).items.push(item);
      }
    });
    const countrySuggestions = sortByPriority(Array.from(countryMap.values())).slice(0, 8);

    // 3. Group Continents by Continent field
    const continentMap = new Map();
    filtered.forEach((item) => {
      const continent = item.continent || "";
      if (continent && includes(continent)) {
        if (!continentMap.has(continent)) {
          continentMap.set(continent, {
            displayValue: continent,
            searchTerm: continent,
            type: "continent",
            items: [],
          });
        }
        continentMap.get(continent).items.push(item);
      }
    });
    const continentSuggestions = sortByPriority(Array.from(continentMap.values())).slice(0, 8);

    // Combine all three categories and prioritize by "starts with"
    const allSuggestions = [
      ...citySuggestions,
      ...countrySuggestions,
      ...continentSuggestions,
    ].sort((a, b) => {
      const aStarts = startsWith(a.displayValue);
      const bStarts = startsWith(b.displayValue);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      return 0;
    }).slice(0, 8);

    setSuggestions(allSuggestions);
    setShowSuggestions(allSuggestions.length > 0);
    setSelectedIndex(-1);
  }, [allSearchData]);

  // Debounce search with 300ms delay
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      performSearch(searchTerm);
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchTerm, performSearch]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setShowSuggestions(true);
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (event) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (event.key === "Enter") {
        triggerSearch(searchTerm);
      }
      return;
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        event.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        event.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          const selected = suggestions[selectedIndex];
          const searchValue = selected.searchTerm || selected.displayValue;
          setSearchTerm(searchValue);
          triggerSearch(searchValue);
          setShowSuggestions(false);
        } else {
          triggerSearch(searchTerm);
        }
        break;
      case "Escape":
        event.preventDefault();
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleSuggestionClick = (suggestion) => {
    const searchValue = suggestion.searchTerm || suggestion.displayValue;
    setSearchTerm(searchValue);
    triggerSearch(searchValue);
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  const triggerSearch = (term) => {
    localStorage.setItem("searchTerm", term);
    const currentPath = location.pathname;
    if (term && term.trim() !== "") {
      navigate(`${currentPath}?q=${encodeURIComponent(term)}`);
    } else {
      navigate(currentPath);
    }
    executeSearch(term);
    window.dispatchEvent(
      new CustomEvent("searchTermChanged", {
        detail: { searchTerm: term },
      })
    );
  };

  const handleSearchClick = () => {
    triggerSearch(searchTerm);
  };


  // Effect to navigate to image when carouselData or imageId changes
  useEffect(() => {
    if (imageId.imageId && carouselData.length > 0) {
      const index = carouselData.findIndex(
        (image) => image.id === imageId.imageId
      );
      if (index !== -1) {
        setSelectedImageIndex(index);
        if (sliderRef.current) {
          sliderRef.current.slickGoTo(index);
        }
      }
    }
  }, [carouselData, imageId.imageId]);

  // Handler for stripe selector - syncs with carousel
  const handleStripeSelect = (index) => {
    setSelectedImageIndex(index);
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(index);
    }
  };

  // Slider settings
  const sliderSettings = {
    infinite: false,
    speed: 500,
    slidesToShow: 15,
    variableWidth: true,
    swipeToSlide: true,
    dots: false,
    arrows: true,
    centerMode: true,
    centerPadding: "0px",
    focusOnSelect: true,
    lazyLoad: true,
    nextArrow: (
      <ArrowCircleRightIcon
        sx={{
          fontSize: 40,
          color: "white",
          opacity: 0.6,
          "&:hover": { color: "white", opacity: 0.8 },
        }}
      />
    ),
    prevArrow: (
      <ArrowCircleLeftIcon
        sx={{
          fontSize: 40,
          color: "white",
          opacity: 0.6,
          "&:hover": { color: "white", opacity: 0.8 },
        }}
      />
    ),
    afterChange: (current) => setSelectedImageIndex(current),
  };

  // Effect to listen for search term changes from Navbar/SegmentsPage
  useEffect(() => {
    const handleSearchChange = (e) => {
      const newSearchTerm = e.detail.searchTerm;
      console.log("Hero: Received searchTermChanged event:", newSearchTerm);
      executeSearch(newSearchTerm);
    };
    
    window.addEventListener('searchTermChanged', handleSearchChange);
    
    return () => {
      window.removeEventListener('searchTermChanged', handleSearchChange);
    };
  }, []); // Runs once on mount to add listener

  const handleFileUpload = async (imageId, file) => {
    try {
      const filePath = `locations/${imageId}/${file.name}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from('form_responses')
        .update({ img_url: publicUrl })
        .eq('id', imageId);

      if (updateError) throw updateError;
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  const handleUploadClick = (imageId) => {
    console.log("ImageId", imageId);
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "image/*";
    fileInput.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        handleFileUpload(imageId, file);
      }
    };
    fileInput.click();
  };


  return (
    <div className="hero">
      <div style={{
        padding: "5px",
        display: "flex",
        justifyContent: "center",
        width: "100%",
        maxWidth: "1200px",
        margin: "0 auto"
      }}>
        <div 
          ref={searchRef}
          style={{ width: "100%", maxWidth: "500px", position: "relative" }}
        >
          <Search>
            <StyledInputBase
              placeholder="Search"
              inputProps={{ "aria-label": "search" }}
              value={searchTerm}
              onChange={handleSearch}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                if (suggestions.length > 0) {
                  setShowSuggestions(true);
                }
              }}
            />
            <SearchIconWrapper onClick={handleSearchClick}>
              <SearchIcon style={{ color: "white" }} />
            </SearchIconWrapper>
          </Search>
          {showSuggestions && suggestions.length > 0 && (
            <div
              ref={suggestionsRef}
              style={{
                position: "absolute",
                top: "100%",
                left: "0",
                right: "0",
                marginTop: "8px",
                backgroundColor: colors.bgTertiary,
                borderRadius: colors.radiusXl,
                border: `1px solid ${colors.borderLight}`,
                maxHeight: "300px",
                overflowY: "auto",
                zIndex: 1000,
                boxShadow: colors.shadowTooltip,
              }}
            >
              {suggestions.map((suggestion, index) => (
                <div
                  key={`${suggestion.type}-${suggestion.displayValue}-${index}`}
                  onClick={() => handleSuggestionClick(suggestion)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  style={{
                    padding: "12px 16px",
                    cursor: "pointer",
                    color: colors.textPrimary,
                    backgroundColor: index === selectedIndex ? colors.bgTertiaryHover : "transparent",
                    borderBottom: index < suggestions.length - 1 ? "1px solid rgba(255, 255, 255, 0.1)" : "none",
                    transition: "background-color 0.2s",
                    borderRadius: index === 0 ? `${colors.radiusXl}px ${colors.radiusXl}px 0 0` : index === suggestions.length - 1 ? `0 0 ${colors.radiusXl}px ${colors.radiusXl}px` : "0",
                  }}
                >
                  <div style={{ fontSize: "12px", color: colors.textWhite70 }}>
                    {suggestion.displayValue}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <section id="intro" className="about-me-content">
        <div className="carousel-container">
          <Slider ref={sliderRef} {...sliderSettings}>
            {carouselData.map((image, index) => (
              <div key={image.id || image.legacy_id}>
                {image.hero_image == null &&
                (!image.image_url || image.image_url.length === 0) ? (
                  <div className={`slide-item no-image-wrap`}>
                    <div className="image-card">
                      <div className="no-image-info">
                        <h6>Title: {image.title}</h6>
                        <p style={{ marginTop: 24 }}>Information:</p>
                        <p>
                          {image.segment}, {image.country}, {image.continent}
                        </p>
                        <p>Lat: {image.latitude}</p>
                        <p>Long: {image.longitude}</p>
                        <div className={`note-text ${index === selectedImageIndex ? "active" : ""}`}>
                          We currently don't have a picture of this section of the Wall - if you've been here please upload a photo.
                        </div>
                        <div className={`upload-photo-wrap ${index === selectedImageIndex ? "active" : ""}`}>
                          <button
                            className="btn btn-primary mt-4"
                            onClick={() => handleUploadClick(image.id)}
                          >
                            Upload a Photo
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={`slide-item ${index === selectedImageIndex ? "active" : ""}`}>
                    <div className="image-card">
                      <img
                        src={
                          image.hero_image
                            ? image.hero_image
                            : (Array.isArray(image.image_url) ? image.image_url[0] : image.image_url)
                        }
                        alt={image.location || image.title}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </Slider>
        </div>

        {carouselData.length > 0 && (
          <div className="fixed-bottom-info">
            <div className="current-image-info">
              <h6>{carouselData[selectedImageIndex]?.title}</h6>
              <p>{carouselData[selectedImageIndex]?.location}</p>
            </div>
            <StripeSelector
              items={carouselData}
              selectedIndex={selectedImageIndex}
              onSelect={handleStripeSelect}
            />
          </div>
        )}
      </section>
    </div>
  );
};

export default Hero;
